import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { ClientMessage, GameSettings, PlayerTarget, PrivateState, Role, TiebreakChoice } from '@shum/shared';
import type { RoomState } from '../lib/socket';
import { ROLE_DESCRIPTION, ROLE_DISPLAY_NAME } from '@shum/shared';
import { useCountdown, useSignedCountdown, formatRemaining, formatSigned } from '../lib/format';
import { api } from '../lib/api';

interface GameScreensProps {
  state: RoomState;
  send: (m: ClientMessage) => boolean;
  privateState: PrivateState | null;
  roomId: string;
  isCreator: boolean;
  settings?: GameSettings;
}

export function GameScreens({ state, send, privateState, roomId, isCreator, settings }: GameScreensProps) {
  const reveal = state.roleReveal;
  // Role reveal: prefer the dedicated role.reveal event for the timed phase.
  if (privateState?.screen === 'role_reveal' && reveal) {
    return (
      <RoleReveal
        role={reveal.role}
        displayName={reveal.displayName}
        description={reveal.description}
        mafiaTeammates={reveal.mafiaTeammates}
        privateState={privateState}
        send={send}
      />
    );
  }

  if (!privateState) {
    return <div className="night"><div className="moon" /><h1>Подготовка…</h1></div>;
  }

  switch (privateState.screen) {
    case 'role_reveal':
      return (
        <RoleReveal
          role={privateState.role ?? 'civilian'}
          displayName=""
          description=""
          privateState={privateState}
          send={send}
        />
      );
    case 'night_neutral':
      return <NightNeutral text={privateState.text} endsAt={privateState.endsAt} />;
    case 'mafia_action':
      return <MafiaAction privateState={privateState} send={send} />;
    case 'mafia_don_action':
      return <MafiaDonAction privateState={privateState} send={send} />;
    case 'mafia_don_result':
      return <MafiaDonResult name={privateState.donResult?.targetName ?? ''} result={privateState.donResult?.result ?? 'not_detective'} endsAt={privateState.endsAt} />;
    case 'doctor_action':
      return (
        <TargetAction
          title="Кого спасти?"
          subtitle="Доктор выбирает пациента на эту ночь."
          targets={privateState.allowedTargets ?? []}
          selectedId={privateState.selectedTargetId ?? null}
          endsAt={privateState.endsAt}
          confirmed={privateState.doctorConfirmed}
          onSubmit={(id) => send({ type: 'night.doctor.save.submit', targetPlayerId: id })}
          onConfirm={() =>
            send({ type: 'night.doctor.save.confirm', targetPlayerId: privateState.selectedTargetId ?? '' })
          }
        />
      );
    case 'detective_action':
      return (
        <TargetAction
          title="Кого проверить?"
          subtitle="Комиссар узнает, мафия это или нет."
          targets={privateState.allowedTargets ?? []}
          selectedId={privateState.selectedTargetId ?? null}
          endsAt={privateState.endsAt}
          confirmed={privateState.detectiveConfirmed}
          onSubmit={(id) => send({ type: 'night.detective.check.submit', targetPlayerId: id })}
          onConfirm={() =>
            send({ type: 'night.detective.check.confirm', targetPlayerId: privateState.selectedTargetId ?? '' })
          }
        />
      );
    case 'detective_result':
      return <DetectiveResult name={privateState.detectiveResult?.targetName ?? ''} result={privateState.detectiveResult?.result ?? 'not_mafia'} endsAt={privateState.endsAt} />;
    case 'discussion':
      return <DayPhase privateState={privateState} send={send} />;
    case 'voting':
      return <Voting privateState={privateState} send={send} isCreator={isCreator} />;
    case 'tiebreak':
      return <Tiebreak privateState={privateState} send={send} />;
    case 'last_words':
      return <LastWords privateState={privateState} />;
    case 'dead':
      return <DeadScreen privateState={privateState} send={send} />;
    case 'game_over':
      return (
        <GameOver
          winner={privateState.winner ?? null}
          roomId={roomId}
          send={send}
          isCreator={isCreator}
          settings={settings}
          error={state.error?.message ?? null}
        />
      );
    default:
      return <NightNeutral text="Ждём…" endsAt={privateState.endsAt} />;
  }
}

function Timer({ endsAt }: { endsAt: string | null }) {
  const remaining = useCountdown(endsAt);
  return <div className={`timer ${remaining <= 5 ? 'warn' : ''}`}>{formatRemaining(remaining)}</div>;
}

function RoleReveal({
  role,
  displayName,
  description,
  mafiaTeammates,
  privateState,
  send,
}: {
  role: Role;
  displayName: string;
  description: string;
  mafiaTeammates?: PlayerTarget[];
  privateState?: PrivateState;
  send: (m: ClientMessage) => boolean;
}) {
  const name = displayName || ROLE_DISPLAY_NAME[role];
  const desc = description || ROLE_DESCRIPTION[role];
  const acknowledged = privateState?.roleAcknowledged ?? false;
  const ackCount = privateState?.roleAckCount ?? 0;
  const ackRequired = privateState?.roleAckRequired ?? 0;
  const allAcknowledged = ackRequired > 0 && ackCount >= ackRequired;
  const remaining = useCountdown(allAcknowledged ? privateState?.roleAckEndsAt ?? null : null);

  return (
    <div className="reveal">
      <span className="reveal-team">Ваша роль</span>
      {/* Everyone sees the same green colour so no one can read a role from the screen. */}
      <div className="reveal-role town">{name}</div>
      <p className="reveal-desc">{desc}</p>
      {mafiaTeammates && mafiaTeammates.length > 0 && (
        <div className="reveal-team-list">
          {mafiaTeammates.map((m) => m.name).join(', ')}
        </div>
      )}

      {!acknowledged ? (
        <button
          className="btn-primary lg"
          onClick={() => {
            if (!send({ type: 'role.acknowledge' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
          }}
        >
          Понятно
        </button>
      ) : allAcknowledged ? (
        <div className="reveal-countdown">
          <span className="label">Игра начнётся через</span>
          <div className="timer">{formatRemaining(remaining)}</div>
        </div>
      ) : (
        <div className="reveal-countdown">
          <span className="label">Ждём остальных</span>
          <div className="timer">{Math.min(ackCount, ackRequired)}/{ackRequired}</div>
        </div>
      )}

      <span className="reveal-team">
        {acknowledged
          ? allAcknowledged ? 'Все готовы' : `Ждём остальных · ${Math.min(ackCount, ackRequired)}/${ackRequired}`
          : 'Нажмите «Понятно», когда запомните роль'}
      </span>
    </div>
  );
}

function NightNeutral({ text, endsAt }: { text?: string; endsAt: string | null }) {
  return (
    <div className="night">
      <div className="moon" />
      <h1>Ночь</h1>
      <p>{text ?? 'Не открывайте глаза. Идёт ночь.'}</p>
      {endsAt && <Timer endsAt={endsAt} />}
    </div>
  );
}

function TargetAction({
  title,
  subtitle,
  targets,
  selectedId,
  endsAt,
  danger,
  confirmed,
  onSubmit,
  onConfirm,
}: {
  title: string;
  subtitle: string;
  targets: { playerId: string; name: string }[];
  selectedId: string | null;
  endsAt: string | null;
  danger?: boolean;
  confirmed?: boolean;
  onSubmit: (id: string) => void;
  onConfirm?: () => void;
}) {
  const selectedName = targets.find((t) => t.playerId === selectedId)?.name ?? '';
  return (
    <div className="action">
      <div className="action-head">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      {endsAt && <Timer endsAt={endsAt} />}
      <div className="targets">
        {targets.map((t) => (
          <button
            key={t.playerId}
            className={`target ${selectedId === t.playerId ? 'sel' : ''} ${danger ? 'danger' : ''}`}
            onClick={() => onSubmit(t.playerId)}
          >
            <span className="tn">{t.name}</span>
            {selectedId === t.playerId && <span className="tk">Выбрано</span>}
          </button>
        ))}
      </div>
      {onConfirm && (
        <div className="mafia-confirm">
          <div className="confirm-row">
            <button
              className="btn-primary lg"
              disabled={!selectedId || confirmed}
              onClick={onConfirm}
            >
              {confirmed ? 'Вы подтвердили' : selectedName ? `Подтвердить: ${selectedName}` : 'Сначала выберите цель'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MafiaAction({ privateState, send }: { privateState: PrivateState; send: (m: ClientMessage) => boolean }) {
  const selectedId = privateState.selectedTargetId ?? null;
  const selectedName = privateState.allowedTargets?.find((t) => t.playerId === selectedId)?.name ?? '';
  const confirmed = privateState.mafiaConfirmations ?? [];
  const confirmedNames = confirmed.map((p) => p.name).join(', ');
  const confirmCount = privateState.mafiaConfirmCount ?? confirmed.length;
  const required = privateState.mafiaRequiredConfirmations ?? 1;

  return (
    <div className="action">
      <div className="action-head">
        <h1>Выберите жертву</h1>
        <p>
          {privateState.mafiaTeammates?.length
            ? `Команда: ${privateState.mafiaTeammates.map((m) => m.name).join(', ')}`
            : 'Вы действуете в одиночку'}
        </p>
      </div>
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
      <div className="targets">
        {(privateState.allowedTargets ?? []).map((t) => (
          <button
            key={t.playerId}
            className={`target ${selectedId === t.playerId ? 'sel' : ''} danger`}
            onClick={() => send({ type: 'night.mafia.target.select', targetPlayerId: t.playerId })}
          >
            <span className="tn">{t.name}</span>
            {selectedId === t.playerId && <span className="tk">Цель</span>}
          </button>
        ))}
      </div>
      <div className="mafia-confirm">
        <div className="confirm-row">
          <button
            className="btn-primary lg"
            disabled={!selectedId || privateState.mafiaConfirmedByYou}
            onClick={() => {
              if (selectedId) send({ type: 'night.mafia.target.confirm', targetPlayerId: selectedId });
            }}
          >
            {privateState.mafiaConfirmedByYou ? 'Вы подтвердили' : selectedName ? `Подтвердить: ${selectedName}` : 'Сначала выберите цель'}
          </button>
          <span className="label">
            {confirmCount}/{required}
          </span>
        </div>
        <p className="confirm-meta">
          {confirmedNames ? `Подтвердили: ${confirmedNames}.` : 'Подтверждений пока нет.'}
        </p>
      </div>
    </div>
  );
}

function MafiaDonAction({ privateState, send }: { privateState: PrivateState; send: (m: ClientMessage) => boolean }) {
  const selectedId = privateState.selectedTargetId ?? null;
  const selectedName = privateState.allowedTargets?.find((t) => t.playerId === selectedId)?.name ?? '';
  return (
    <div className="action">
      <div className="action-head">
        <h1>Кто комиссар?</h1>
        <p>Дом мафии вычисляет комиссара среди горожан.</p>
      </div>
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
      <div className="targets">
        {(privateState.allowedTargets ?? []).map((t) => (
          <button
            key={t.playerId}
            className={`target ${selectedId === t.playerId ? 'sel' : ''} danger`}
            onClick={() => send({ type: 'night.mafia.don.check.submit', targetPlayerId: t.playerId })}
          >
            <span className="tn">{t.name}</span>
            {selectedId === t.playerId && <span className="tk">Подозрение</span>}
          </button>
        ))}
      </div>
      <div className="mafia-confirm">
        <div className="confirm-row">
          <button
            className="btn-primary lg"
            disabled={!selectedId || privateState.donConfirmed}
            onClick={() => {
              if (selectedId) send({ type: 'night.mafia.don.check.confirm', targetPlayerId: selectedId });
            }}
          >
            {privateState.donConfirmed ? 'Вы подтвердили' : selectedName ? `Проверить: ${selectedName}` : 'Сначала выберите цель'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MafiaDonResult({ name, result, endsAt }: { name: string; result: 'detective' | 'not_detective'; endsAt: string | null }) {
  return (
    <div className="detective-result">
      <span className="label">Дон проверяет</span>
      <p style={{ fontSize: 20, color: 'var(--ink-dim)' }}>{name}</p>
      <div className={`big ${result === 'detective' ? 'mafia' : 'town'}`}>
        {result === 'detective' ? 'КОМИССАР' : 'НЕ КОМИССАР'}
      </div>
      {endsAt && <Timer endsAt={endsAt} />}
    </div>
  );
}

function DetectiveResult({ name, result, endsAt }: { name: string; result: 'mafia' | 'not_mafia'; endsAt: string | null }) {
  return (
    <div className="detective-result">
      <span className="label">Проверка</span>
      <p style={{ fontSize: 20, color: 'var(--ink-dim)' }}>{name}</p>
      <div className={`big ${result === 'mafia' ? 'mafia' : 'town'}`}>{result === 'mafia' ? 'МАФИЯ' : 'НЕ МАФИЯ'}</div>
      {endsAt && <Timer endsAt={endsAt} />}
    </div>
  );
}

function DayPhase({ privateState, send }: { privateState: PrivateState; send: (m: ClientMessage) => boolean }) {
  const speakerTurn = privateState.phase === 'DISCUSSION' ? privateState.speakerTurn : null;
  const speakerRemaining = useCountdown(speakerTurn?.turnEndsAt ?? null);
  const speakerState = speakerRemaining <= 3 ? 'danger' : speakerRemaining <= 10 ? 'warn' : 'go';
  useSpeakerBeeps(speakerTurn, speakerRemaining);
  const turnKey = speakerTurn ? `${speakerTurn.playerId}:${speakerTurn.turnStartedAt}` : '';
  const [doneTurnKey, setDoneTurnKey] = useState<string | null>(null);

  useEffect(() => {
    if (turnKey && doneTurnKey && doneTurnKey !== turnKey) setDoneTurnKey(null);
  }, [doneTurnKey, turnKey]);

  return (
    <div className={`day ${speakerTurn?.isYou ? `speaker-live ${speakerState}` : ''}`}>
      <h1>День {privateState.dayNumber}</h1>
      {speakerTurn && (
        <div className="speaker-turn" data-state={speakerState} data-you={speakerTurn.isYou ? '1' : '0'}>
          <span className="label">Говорит {speakerTurn.index + 1}/{speakerTurn.total}</span>
          <h2>{speakerTurn.isYou ? 'Вы говорите' : speakerTurn.name}</h2>
          <div className="speaker-clock">{formatRemaining(speakerRemaining)}</div>
          {speakerTurn.isYou && (
            <button
              className="btn-primary speaker-done"
              disabled={doneTurnKey === turnKey}
              onClick={() => {
                setDoneTurnKey(turnKey);
                if (!send({ type: 'day.discussion.done' })) {
                  setDoneTurnKey(null);
                  alert('Нет связи с комнатой. Попробуйте ещё раз.');
                }
              }}
            >
              {doneTurnKey === turnKey ? 'Передаём ход…' : 'Я всё'}
            </button>
          )}
        </div>
      )}
      {privateState.lastNightDeath?.name ? (
        <p className="announce">
          Этой ночью убили <span className="who">{privateState.lastNightDeath.name}</span>.
        </p>
      ) : (
        <p className="announce">
          <span className="none">Этой ночью никого не убили.</span>
        </p>
      )}
      {privateState.phase === 'DAY_RESOLUTION' && (
        privateState.lastElimination?.name ? (
          <p className="announce">
            Днём убили <span className="who">{privateState.lastElimination.name}</span>.
          </p>
        ) : (
          <p className="announce">
            <span className="none">Днём никого не выбрали.</span>
          </p>
        )
      )}
      {privateState.text && <p className="muted">{privateState.text}</p>}
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
      <div>
        <span className="label">Живые игроки</span>
        <div className="targets" style={{ marginTop: 12 }}>
          {privateState.livingPlayers.map((p) => (
            <div className="target" key={p.playerId} style={{ cursor: 'default' }}>
              <span className="tn">{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function useSpeakerBeeps(
  speakerTurn: PrivateState['speakerTurn'],
  remainingSeconds: number,
) {
  const beepedTurn = useRef<string | null>(null);

  useEffect(() => {
    if (!speakerTurn?.isYou) return;
    const turnKey = `${speakerTurn.playerId}:${speakerTurn.turnStartedAt}`;
    if (remainingSeconds > 3 || remainingSeconds <= 0 || beepedTurn.current === turnKey) return;
    beepedTurn.current = turnKey;
    playThreeBeeps();
  }, [remainingSeconds, speakerTurn]);
}

function playThreeBeeps() {
  const AudioContextCtor = window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) return;
  let ctx: AudioContext;
  try {
    ctx = new AudioContextCtor();
  } catch {
    return;
  }
  const now = ctx.currentTime;
  for (let i = 0; i < 3; i += 1) {
    const start = now + i * 0.24;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.24, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.14);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(start);
    oscillator.stop(start + 0.16);
  }
  window.setTimeout(() => void ctx.close().catch(() => {}), 1100);
}

function Voting({ privateState, send, isCreator }: { privateState: PrivateState; send: (m: ClientMessage) => boolean; isCreator: boolean }) {
  type VoteChoice = string | 'abstain' | null;
  const voteChoice: VoteChoice = privateState.voteSubmitted
    ? privateState.selectedTargetId ?? 'abstain'
    : null;
  const [voted, setVoted] = useState<VoteChoice>(voteChoice);
  useEffect(() => {
    setVoted(voteChoice);
  }, [voteChoice, privateState.phase]);

  function vote(id: string | null) {
    setVoted(id ?? 'abstain');
    send({ type: 'day.vote.submit', targetPlayerId: id });
  }

  const totals = privateState.voteTotals ?? [];
  const totalVotes = totals.reduce((a, t) => a + t.votes, 0);
  const submitted = privateState.voteSubmittedCount ?? totalVotes;
  const required = privateState.voteRequiredCount ?? privateState.livingPlayers.length + 1;
  const manual = privateState.votingManual;
  const remaining = useSignedCountdown(privateState.endsAt);
  const overtime = remaining < 0;
  const canEnd = Boolean(privateState.votingCanEnd) || overtime;

  return (
    <div className="action">
      <div className="action-head">
        <h1>Голосование</h1>
        <p>До конца таймера ведущий завершает голосование только после выбора всех игроков. После таймера можно завершить с неполными голосами.</p>
      </div>
      <div className={`timer ${overtime ? 'warn' : ''}`} data-overtime={overtime ? '1' : '0'}>
        {manual ? formatSigned(remaining) : formatRemaining(Math.max(0, remaining))}
      </div>
      {overtime && <p className="muted">Время вышло — голосование завершает ведущий.</p>}
      <div className="targets">
        {privateState.livingPlayers.map((p) => (
          <button key={p.playerId} className={`target ${voted === p.playerId ? 'sel' : ''}`} onClick={() => vote(p.playerId)}>
            <span className="tn">{p.name}</span>
            {voted === p.playerId && <span className="tk">Голос</span>}
          </button>
        ))}
      </div>
      <button className={`btn-ghost abstain ${voted === 'abstain' ? 'sel' : ''}`} onClick={() => vote(null)}>
        {voted === 'abstain' ? 'Воздержался' : 'Воздержаться'}
      </button>
      <div>
        <span className="label">Результаты · {submitted}/{required} выбрали</span>
        <div className="vote-totals">
          {totals.length === 0 && <p className="muted">Пока никто не голосовал.</p>}
          {totals.map((t) => {
            const voters = t.voters ?? [];
            return (
              <div className="vote-row" key={t.playerId ?? 'abstain'}>
                <span className="vn">{t.name}</span>
                <span className="vc">{t.votes}</span>
                {voters.length > 0 && <span className="vv">{voters.map((v) => v.name).join(', ')}</span>}
              </div>
            );
          })}
        </div>
      </div>
      {isCreator && (
        <button
          className="btn-primary lg block"
          disabled={!canEnd}
          onClick={() => {
            if (!send({ type: 'day.end_voting' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
          }}
        >
          {canEnd ? 'Завершить голосование' : `Ждём голоса · ${submitted}/${required}`}
        </button>
      )}
    </div>
  );
}

function Tiebreak({ privateState, send }: { privateState: PrivateState; send: (m: ClientMessage) => boolean }) {
  const candidates = privateState.tiebreakCandidates ?? [];
  const yourVote = privateState.tiebreakVote ?? null;
  const kill = privateState.tiebreakKill ?? 0;
  const keep = privateState.tiebreakKeep ?? 0;
  const voters = privateState.tiebreakVoters ?? 0;

  function cast(choice: TiebreakChoice) {
    send({ type: 'day.tiebreak.vote', choice });
  }

  return (
    <div className="action">
      <div className="action-head">
        <h1>Голоса разделились</h1>
        <p>Решите судьбу обоих кандидатов. При равенстве оба остаются.</p>
      </div>
      {privateState.endsAt && <Timer endsAt={privateState.endsAt} />}
      <div className="tie-candidates">
        {candidates.map((c) => (
          <div className="target" key={c.playerId} style={{ cursor: 'default' }}>
            <span className="tn">{c.name}</span>
          </div>
        ))}
      </div>
      <div className="tie-actions">
        <button
          className={`btn-primary lg ${yourVote === 'kill' ? 'sel' : ''}`}
          disabled={yourVote !== null}
          onClick={() => cast('kill')}
        >
          {yourVote === 'kill' ? 'Вы выбрали: убрать обоих' : 'Убрать обоих'}
        </button>
        <button
          className={`btn-ghost lg ${yourVote === 'keep' ? 'sel' : ''}`}
          disabled={yourVote !== null}
          onClick={() => cast('keep')}
        >
          {yourVote === 'keep' ? 'Вы выбрали: оставить обоих' : 'Оставить обоих'}
        </button>
      </div>
      <div>
        <span className="label">Голоса · {kill + keep}/{voters}</span>
        <div className="vote-totals">
          <div className="vote-row">
            <span className="vn">Убрать обоих</span>
            <span className="vc">{kill}</span>
          </div>
          <div className="vote-row">
            <span className="vn">Оставить обоих</span>
            <span className="vc">{keep}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LastWords({ privateState }: { privateState: PrivateState }) {
  const speakers = privateState.lastWordsPlayers ?? [];
  const speaker = privateState.lastWordsSpeaker ?? speakers[privateState.lastWordsIndex ?? 0] ?? null;
  const isYou = privateState.lastWordsIsYou ?? false;
  const current = (privateState.lastWordsIndex ?? 0) + 1;
  const total = privateState.lastWordsTotal ?? speakers.length;
  return (
    <div className="last-words">
      <span className="label">Последнее слово{total > 1 ? ` · ${current}/${total}` : ''}</span>
      <h1>{isYou ? 'Ваше последнее слово' : speaker ? `${speaker.name} говорит` : 'Последнее слово'}</h1>
      <p className="muted">{isYou ? 'У вас есть 30 секунд. Говорите.' : 'У игрока есть 30 секунд на последнее слово.'}</p>
      <Timer endsAt={privateState.endsAt} />
      {speaker && (
        <div className="targets" style={{ marginTop: 12 }}>
          <div className="target" style={{ cursor: 'default' }}>
            <span className="tn">{speaker.name}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function DeadScreen({ privateState, send }: { privateState: PrivateState; send: (m: ClientMessage) => boolean }) {
  return (
    <div className="dead-screen" aria-live="polite">
      <h1>Вы убиты</h1>
      {privateState.deadRoleCanAdvance && (
        <>
          <p className="muted">Сейчас пропускается ход вашей роли.</p>
          <Timer endsAt={privateState.endsAt} />
          <button
            className="btn-primary lg"
            onClick={() => {
              if (!send({ type: 'night.dead_role.advance' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
            }}
          >
            Дальше
          </button>
        </>
      )}
    </div>
  );
}

function GameOver({
  winner,
  roomId,
  send,
  isCreator,
  settings,
  error,
}: {
  winner: 'mafia' | 'town' | null;
  roomId: string;
  send: (m: ClientMessage) => boolean;
  isCreator: boolean;
  settings?: GameSettings;
  error: string | null;
}) {
  const [history, setHistory] = useState<{ games: { gameId: string; roomId: string; startedAt: string; finishedAt: string | null; winner: string | null; playerCount: number }[] } | null>(null);
  useEffect(() => {
    api
      .roomHistory(roomId)
      .then(setHistory)
      .catch(() => {});
  }, [roomId]);

  const lastGame = history?.games?.[0];
  return (
    <div className="over">
      <span className="label">Игра окончена</span>
      <h1 className={winner ?? ''}>{winner === 'mafia' ? 'Победила мафия' : 'Победили мирные'}</h1>
      <p className="sub">
        {winner === 'mafia' ? 'Город пал. Мафия перехитрила всех.' : 'Город вычислил всех мафиози. Добро восторжествовало.'}
      </p>
      {lastGame && (
        <p className="sub mono" style={{ fontSize: 12, letterSpacing: '0.1em' }}>
          {new Date(lastGame.startedAt).toLocaleString('ru-RU')} · {lastGame.playerCount} игроков · код {lastGame.roomId}
        </p>
      )}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginTop: 16 }}>
        {isCreator ? (
          <>
            <button
              type="button"
              className="btn-ghost lg"
              onClick={() => {
                if (!send({ type: 'room.start' })) alert('Нет связи с комнатой. Попробуйте ещё раз.');
              }}
            >
              Сыграть снова
            </button>
            <Link to="/play/mafia/create" state={{ settings }} className="btn-primary lg">
              Новая игра →
            </Link>
          </>
        ) : (
          <button type="button" className="btn-ghost lg" disabled>
            Ждём ведущего
          </button>
        )}
      </div>
      {error && <div className="err">{error}</div>}
    </div>
  );
}
